import React from 'react';
import {
  AppBar, Avatar, Badge, Box, Button, Collapse, Divider, Drawer, IconButton, List, ListItemButton, Menu, MenuItem, Toolbar, Tooltip, Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useNavigate, useLocation } from 'react-router-dom';
import { MdLogout } from 'react-icons/md';
import { getData } from '../Webservice/webservice';
import Swal from 'sweetalert2';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { pathOf, label } from './menuRoutes';
import { useTour } from './Tour/TourProvider';

const FONT = 'Mulish, sans-serif';
const GOLD = '#FFD102';

// ---- motion ----
const slideDown = keyframes`from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;
const fadeUp = keyframes`from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); }`;
const shimmer = keyframes`0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; }`;
const ring = keyframes`0%, 55%, 100% { transform: rotate(0); } 8% { transform: rotate(16deg); } 16% { transform: rotate(-14deg); } 24% { transform: rotate(11deg); } 32% { transform: rotate(-9deg); } 40% { transform: rotate(5deg); } 48% { transform: rotate(-3deg); }`;
const pulse = keyframes`0% { box-shadow: 0 0 0 0 rgba(239,68,68,.55); } 70% { box-shadow: 0 0 0 9px rgba(239,68,68,0); } 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }`;
const float = keyframes`0%, 100% { transform: translateY(0); } 50% { transform: translateY(-2px); }`;

// which page each menu opens: shared with the guided tour (menuRoutes.js)
const iconOf = (name, size = 20) => {
  const sx = { fontSize: size };
  const map = {
    Dashboard: <DashboardIcon sx={sx} />, Products: <InventoryIcon sx={sx} />, ProductManagement: <InventoryIcon sx={sx} />, SellerProductRequest: <FactCheckIcon sx={sx} />,
    Categories: <InventoryIcon sx={sx} />, Expired: <InventoryIcon sx={sx} />, Operations: <SettingsIcon sx={sx} />, Orders: <ShoppingCartIcon sx={sx} />,
    'Online Orders': <ShoppingCartIcon sx={sx} />, Coupons: <LocalOfferIcon sx={sx} />, Logs: <HistoryIcon sx={sx} />, ActivityLog: <HistoryIcon sx={sx} />,
    LoginLogs: <FactCheckIcon sx={sx} />, Payout: <AccountBalanceWalletIcon sx={sx} />, Payouts: <AccountBalanceWalletIcon sx={sx} />, SellerPayout: <AccountBalanceWalletIcon sx={sx} />,
    'Seller Payout': <AccountBalanceWalletIcon sx={sx} />,
  };
  return map[name] || <SettingsIcon sx={sx} />;
};

const readUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('sellerUser') || localStorage.getItem('loginData') || 'null');
  } catch { return null; }
};

const Navbar = () => {
  const navigate = useNavigate();
  const { openDirectory } = useTour();
  const location = useLocation();
  const [account, setAccount] = React.useState(null);
  const [drawer, setDrawer] = React.useState(false);
  const [openMobile, setOpenMobile] = React.useState({});
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [menuItems, setMenuItems] = React.useState([]);
  const [user, setUser] = React.useState(readUser);
  const [submenu, setSubmenu] = React.useState({ name: null, anchor: null });
  const closeTimer = React.useRef(null);

  // ---- who is signed in (kept fresh when the profile is edited) ----
  React.useEffect(() => {
    const refresh = () => setUser(readUser());
    window.addEventListener('seller-profile-changed', refresh);
    return () => window.removeEventListener('seller-profile-changed', refresh);
  }, []);

  // ---- unread notifications for the bell ----
  const fetchUnreadCount = React.useCallback(async () => {
    try {
      const raw = sessionStorage.getItem('token') || localStorage.getItem('sellerToken');
      const res = await getData('seller/notification/vend', { limit: 1, isRead: false }, raw ? `Bearer ${raw}` : '');
      if (res && res.success) setUnreadCount(res.meta?.unread ?? res.meta?.total ?? 0);
    } catch (error) {
      console.error('Failed to fetch unread count', error);
    }
  }, []);
  React.useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 20000);
    window.addEventListener('seller-notifications-changed', fetchUnreadCount);
    return () => { clearInterval(interval); window.removeEventListener('seller-notifications-changed', fetchUnreadCount); };
  }, [location.pathname, fetchUnreadCount]);

  // ---- the menus this seller can open ----
  React.useEffect(() => {
    try {
      const u = readUser();
      const menus = [];
      (Array.isArray(u?.menus) ? u.menus : []).forEach((menu) => {
        const name = menu.menuName || menu.name;
        menus.push({
          text: label(name),
          icon: iconOf(name),
          path: pathOf(name),
          submenus: (menu.submenus || []).map((sub) => {
            const sName = sub.submenuName || sub.name;
            return { text: label(sName), icon: iconOf(sName, 18), path: pathOf(sName) };
          }),
        });
      });
      setMenuItems(menus);
    } catch (error) {
      console.error('Failed to parse user menu data', error);
      setMenuItems([]);
    }
  }, []);

  const handleLogout = () => {
    setAccount(null);
    Swal.fire({
      title: 'Sign out?',
      text: 'You will need to sign in again to use the seller portal.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2B333A',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Sign out',
    }).then((result) => {
      if (result.isConfirmed) {
        ['token', 'loginData', 'sellerToken', 'sellerUser'].forEach((k) => localStorage.removeItem(k));
        sessionStorage.clear();
        navigate('/login');
      }
    });
  };

  // hovering a menu opens it; leaving closes it after a short grace period
  const openSub = (event, name) => { clearTimeout(closeTimer.current); setSubmenu({ name, anchor: event.currentTarget }); };
  const closeSub = () => { closeTimer.current = setTimeout(() => setSubmenu({ name: null, anchor: null }), 180); };
  const keepSub = () => clearTimeout(closeTimer.current);

  const name = user?.name || user?.firstName || user?.fullName || 'Seller';
  const business = user?.businessName || user?.business_name || '';
  const photo = user?.image || user?.avatar || user?.profileImage || undefined;
  const isActive = (page) => location.pathname === page.path || (page.submenus || []).some((s) => location.pathname === s.path);
  const go = (path) => { if (path) navigate(path); };

  const linkSx = (active, i) => ({
    position: 'relative', flexShrink: 0, fontFamily: FONT, fontSize: '0.92rem', fontWeight: 700, textTransform: 'none', px: 1.75, py: 0.9, borderRadius: '10px',
    color: active ? GOLD : '#cbd5e1', background: active ? 'rgba(255,209,2,.12)' : 'transparent',
    animation: `${fadeUp} .5s ease both`, animationDelay: `${120 + i * 60}ms`,
    transition: 'color .2s, background .2s, transform .2s',
    '& .MuiButton-startIcon': { mr: 0.9, transition: 'transform .25s' },
    '&::after': { content: '""', position: 'absolute', left: 14, right: 14, bottom: 3, height: 2, borderRadius: 2, background: GOLD, transform: active ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'left', transition: 'transform .3s ease' },
    '&:hover': { color: '#fff', background: 'rgba(255,255,255,.09)', transform: 'translateY(-1px)', '&::after': { transform: 'scaleX(1)' }, '& .MuiButton-startIcon': { transform: 'scale(1.18) rotate(-6deg)' } },
  });

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          height: '76px', justifyContent: 'center', boxShadow: '0 6px 26px rgba(15,23,42,.28)',
          background: 'linear-gradient(120deg, #1b2127 0%, #2B333A 55%, #343f48 100%)', backdropFilter: 'blur(10px)',
          animation: `${slideDown} .55s cubic-bezier(.2,.8,.2,1)`,
          '&::after': { content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: '2px', background: `linear-gradient(90deg, transparent, ${GOLD}, transparent, ${GOLD}, transparent)`, backgroundSize: '200% 100%', animation: `${shimmer} 7s linear infinite`, opacity: 0.85 },
        }}
      >
        <Toolbar sx={{ minHeight: 'unset !important', px: { xs: 1.5, md: 3.5 }, gap: 0.5 }}>
          <IconButton aria-label="open menu" onClick={() => setDrawer(true)} sx={{ display: { xs: 'inline-flex', md: 'none' }, color: '#fff', mr: 0.5, '&:hover': { background: 'rgba(255,255,255,.12)' } }}>
            <MenuIcon />
          </IconButton>

          <Box onClick={() => navigate('/')} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: { xs: 1, md: 3 }, transition: 'transform .3s', '&:hover': { transform: 'scale(1.05)' } }}>
            <img src="./img/glamlogo2.png" alt="Seller portal" style={{ height: '50px', width: 'auto' }} />
          </Box>

          {/* menus */}
          <Box data-tour="vendor-navigation" sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, overflowX: 'auto', whiteSpace: 'nowrap', py: 0.5, '&::-webkit-scrollbar': { height: 0 } }}>
            {menuItems.map((page, i) => {
              const active = isActive(page);
              const hasSub = page.submenus.length > 0;
              if (!hasSub) {
                return <Button key={page.text} disabled={!page.path} onClick={() => go(page.path)} startIcon={page.icon} sx={linkSx(active, i)}>{page.text}</Button>;
              }
              const open = submenu.name === page.text;
              return (
                <Box key={page.text} sx={{ flexShrink: 0 }}>
                  <Button
                    onMouseEnter={(e) => openSub(e, page.text)} onMouseLeave={closeSub} onClick={(e) => openSub(e, page.text)}
                    startIcon={page.icon}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18, transition: 'transform .25s', transform: open ? 'rotate(180deg)' : 'none' }} />}
                    sx={linkSx(active || open, i)}
                  >
                    {page.text}
                  </Button>
                  <Menu
                    anchorEl={submenu.anchor} open={open} onClose={() => setSubmenu({ name: null, anchor: null })} disableRestoreFocus
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={{ pointerEvents: 'none' }}
                    MenuListProps={{ onMouseEnter: keepSub, onMouseLeave: closeSub, sx: { py: 0.75 } }}
                    PaperProps={{ onMouseEnter: keepSub, onMouseLeave: closeSub, sx: { pointerEvents: 'auto', mt: 1, minWidth: 230, borderRadius: '14px', overflow: 'hidden', background: 'linear-gradient(160deg, #2B333A, #1f262c)', color: '#fff', border: '1px solid rgba(255,255,255,.1)', boxShadow: '0 18px 40px rgba(0,0,0,.45)', animation: `${fadeUp} .22s ease` } }}
                  >
                    {page.submenus.map((sub, k) => (
                      <MenuItem
                        key={sub.text} disabled={!sub.path} selected={location.pathname === sub.path}
                        onClick={() => { go(sub.path); setSubmenu({ name: null, anchor: null }); }}
                        sx={{
                          gap: 1.25, py: 1.15, px: 2.25, fontFamily: FONT, fontSize: '0.9rem', fontWeight: 600, color: '#cbd5e1', borderLeft: '3px solid transparent', transition: 'all .2s',
                          animation: `${fadeUp} .3s ease both`, animationDelay: `${k * 40}ms`,
                          '&:hover': { background: 'rgba(255,255,255,.08)', color: '#fff', paddingLeft: '22px', borderLeftColor: GOLD },
                          '&.Mui-selected': { background: 'rgba(255,209,2,.14)', color: GOLD, borderLeftColor: GOLD },
                          '&.Mui-selected:hover': { background: 'rgba(255,209,2,.2)' },
                        }}
                      >
                        {sub.icon}{sub.text}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

          {/* help and guided tours */}
          <Tooltip title="Guided tours: start, resume or pick a page">
            <Button
              data-tour="help-button"
              onClick={openDirectory}
              aria-label="Help and guided tours"
              startIcon={<HelpOutlineIcon sx={{ fontSize: 19 }} />}
              sx={{ color: '#fff', border: '1px solid rgba(255,255,255,.3)', borderRadius: '999px', textTransform: 'none', fontWeight: 800, fontFamily: FONT, px: { xs: 1.1, md: 1.75 }, mr: 1, minWidth: 0, '& .MuiButton-startIcon': { mr: { xs: 0, md: 0.75 }, ml: { xs: 0, md: -0.25 } }, '&:hover': { background: 'rgba(255,209,2,.18)', borderColor: GOLD } }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Help &amp; tours</Box>
            </Button>
          </Tooltip>

          {/* bell and account */}
          <Tooltip title={unreadCount ? `${unreadCount} new notification${unreadCount > 1 ? 's' : ''}` : 'Notifications'}>
            <IconButton data-tour="notifications" onClick={() => navigate('/notification')} aria-label="notifications" sx={{ color: '#fff', mr: 0.5, '&:hover': { background: 'rgba(255,255,255,.12)' } }}>
              <Badge badgeContent={unreadCount} max={99} overlap="circular" sx={{ '& .MuiBadge-badge': { fontWeight: 800, fontFamily: FONT, background: '#ef4444', color: '#fff', animation: unreadCount ? `${pulse} 1.8s infinite` : 'none' } }}>
                {unreadCount > 0
                  ? <NotificationsActiveIcon sx={{ fontSize: 28, color: GOLD, transformOrigin: '50% 8%', animation: `${ring} 3.2s ease-in-out infinite` }} />
                  : <NotificationsNoneIcon sx={{ fontSize: 28 }} />}
              </Badge>
            </IconButton>
          </Tooltip>

          <Box
            onClick={(e) => setAccount(e.currentTarget)}
            data-tour="profile-menu"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pl: 0.75, pr: { xs: 0.75, md: 1.5 }, py: 0.6, borderRadius: '14px', cursor: 'pointer', border: '1px solid rgba(255,255,255,.14)', background: 'rgba(255,255,255,.06)', transition: 'all .25s', '&:hover': { background: 'rgba(255,255,255,.13)', borderColor: 'rgba(255,209,2,.5)', transform: 'translateY(-1px)' } }}
          >
            <Avatar src={photo} sx={{ width: 36, height: 36, bgcolor: GOLD, color: '#1b2127', fontWeight: 800, fontFamily: FONT, animation: `${float} 4s ease-in-out infinite` }}>{name.charAt(0).toUpperCase()}</Avatar>
            <Box sx={{ display: { xs: 'none', lg: 'block' }, lineHeight: 1.15, maxWidth: 150 }}>
              <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, color: '#fff' }}>{name}</Typography>
              <Typography noWrap sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>{business || 'Seller'}</Typography>
            </Box>
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#cbd5e1', display: { xs: 'none', md: 'block' }, transition: 'transform .25s', transform: account ? 'rotate(180deg)' : 'none' }} />
          </Box>

          <Menu
            anchorEl={account} open={Boolean(account)} onClose={() => setAccount(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1.5, minWidth: 250, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(15,23,42,.25)', animation: `${fadeUp} .22s ease` } }}
            MenuListProps={{ sx: { p: 0 } }}
          >
            <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center', background: 'linear-gradient(135deg, #2B333A, #3a4650)', color: '#fff' }}>
              <Avatar src={photo} sx={{ width: 46, height: 46, bgcolor: GOLD, color: '#1b2127', fontWeight: 800 }}>{name.charAt(0).toUpperCase()}</Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15 }}>{name}</Typography>
                <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: '#cbd5e1' }}>{user?.email || business || 'Seller account'}</Typography>
              </Box>
            </Box>
            <MenuItem onClick={() => { setAccount(null); navigate('/profile'); }} sx={{ gap: 1.5, py: 1.4, px: 2.25, fontFamily: FONT, fontWeight: 700, transition: 'all .2s', '&:hover': { paddingLeft: '26px', background: '#f8fafc' } }}>
              <PersonOutlineIcon sx={{ fontSize: 20, color: '#64748b' }} /> My profile
            </MenuItem>
            <MenuItem onClick={() => { setAccount(null); navigate('/notification'); }} sx={{ gap: 1.5, py: 1.4, px: 2.25, fontFamily: FONT, fontWeight: 700, transition: 'all .2s', '&:hover': { paddingLeft: '26px', background: '#f8fafc' } }}>
              <Badge color="error" variant="dot" invisible={!unreadCount}><NotificationsNoneIcon sx={{ fontSize: 20, color: '#64748b' }} /></Badge> Notifications
            </MenuItem>
            <MenuItem onClick={() => { setAccount(null); setTimeout(openDirectory, 150); }} sx={{ gap: 1.5, py: 1.4, px: 2.25, fontFamily: FONT, fontWeight: 700 }}>
              <HelpOutlineIcon sx={{ fontSize: 20, color: '#64748b' }} /> Help &amp; tours
            </MenuItem>
            <Divider sx={{ my: '0 !important' }} />
            <MenuItem onClick={handleLogout} sx={{ gap: 1.5, py: 1.4, px: 2.25, fontFamily: FONT, fontWeight: 800, color: '#ef4444', transition: 'all .2s', '&:hover': { paddingLeft: '26px', background: '#fef2f2' } }}>
              <MdLogout size={20} /> Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* phone menu */}
      <Drawer anchor="left" open={drawer} onClose={() => setDrawer(false)} PaperProps={{ sx: { width: 292, background: 'linear-gradient(170deg, #1b2127, #2B333A)', color: '#fff' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <img src="./img/glamlogo2.png" alt="Seller portal" style={{ height: 44 }} />
          <IconButton onClick={() => setDrawer(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ mx: 2, mb: 1.5, p: 1.5, borderRadius: '14px', background: 'rgba(255,255,255,.07)', display: 'flex', gap: 1.25, alignItems: 'center' }}>
          <Avatar src={photo} sx={{ bgcolor: GOLD, color: '#1b2127', fontWeight: 800 }}>{name.charAt(0).toUpperCase()}</Avatar>
          <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14 }}>{name}</Typography><Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}>{business || 'Seller'}</Typography></Box>
        </Box>
        <List sx={{ px: 1.25 }}>
          {menuItems.map((page, i) => {
            const hasSub = page.submenus.length > 0;
            const active = isActive(page);
            const open = !!openMobile[page.text];
            return (
              <Box key={page.text} sx={{ animation: `${fadeUp} .4s ease both`, animationDelay: `${i * 45}ms` }}>
                <ListItemButton
                  disabled={!hasSub && !page.path}
                  onClick={() => (hasSub ? setOpenMobile((m) => ({ ...m, [page.text]: !m[page.text] })) : (go(page.path), setDrawer(false)))}
                  sx={{ borderRadius: '12px', mb: 0.25, gap: 1.25, color: active ? GOLD : '#e2e8f0', background: active ? 'rgba(255,209,2,.12)' : 'transparent', '&:hover': { background: 'rgba(255,255,255,.09)' } }}
                >
                  {page.icon}
                  <Typography sx={{ flex: 1, fontFamily: FONT, fontWeight: 700, fontSize: 14.5 }}>{page.text}</Typography>
                  {hasSub && <KeyboardArrowDownIcon sx={{ transition: 'transform .25s', transform: open ? 'rotate(180deg)' : 'none' }} />}
                </ListItemButton>
                {hasSub && (
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <List disablePadding sx={{ pl: 2.5, mb: 0.5 }}>
                      {page.submenus.map((sub) => (
                        <ListItemButton key={sub.text} disabled={!sub.path} onClick={() => { go(sub.path); setDrawer(false); }}
                          sx={{ borderRadius: '10px', gap: 1.25, py: 0.8, color: location.pathname === sub.path ? GOLD : '#cbd5e1', borderLeft: '2px solid rgba(255,255,255,.12)', '&:hover': { background: 'rgba(255,255,255,.08)' } }}>
                          {sub.icon}<Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 13.5 }}>{sub.text}</Typography>
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={() => { setDrawer(false); handleLogout(); }} startIcon={<MdLogout />} sx={{ m: 2, textTransform: 'none', fontWeight: 800, fontFamily: FONT, color: '#fca5a5', border: '1px solid rgba(252,165,165,.35)', borderRadius: '12px', py: 1 }}>Sign out</Button>
      </Drawer>
    </>
  );
};

export default Navbar;
